<template>
    <div>
        <div class="mb-6 -mt-4">
            <h1>Banking Details</h1>
        </div>

        <ValidationObserver ref="bankingDetailsObserver">
            <div class="mb-6">
                <InputComponent
                    label="Account Name"
                    placeholder="Account Name"
                    name="account_name"
                    v-model="bankingDetails[0].account_name"
                    @change="updateBankingDetails(bankingDetails[0])"
                />
            </div>

            <div class="mb-6">
                <InputComponent
                    label="Account Number"
                    placeholder="Account Number"
                    name="account_number"
                    v-model="bankingDetails[0].account_number"
                    @change="updateBankingDetails(bankingDetails[0])"
                />
            </div>

            <div class="mb-6 w-1/2">
                <InputComponent
                    label="Sort Code"
                    placeholder="00-00-00"
                    name="sort_code"
                    v-model="bankingDetails[0].sort_code"
                    @change="updateBankingDetails(bankingDetails[0])"
                />
            </div>

            <div class="mb-6 flex">
                <div class="w-1/2 mr-2">
                    <InputComponent
                        label="IBAN"
                        placeholder="IBAN"
                        name="iban"
                        rules="optional"
                        v-model="bankingDetails[0].iban"
                        @change="updateBankingDetails(bankingDetails[0])"
                    />
                </div>

                <div class="w-1/2 ml-2">
                    <InputComponent
                        label="BIC/SWIFT"
                        placeholder="BIC/SWIFT"
                        name="bic_swift"
                        rules="optional"
                        v-model="bankingDetails[0].bic_swift"
                        @change="updateBankingDetails(bankingDetails[0])"
                    />
                </div>
            </div>

            <transition-group
                enter-active-class="transition ease-out duration-500 transform"
                enter-class="opacity-0"
                enter-to-class="opacity-100"
            >
                <div v-for="(item, index) in bankingDetails" :key="`key-${index}`">
                    <!-- Only show "additional" bank details -->
                    <div v-if="index > 0">
                        <div class="mb-6 mt-10 flex justify-between">
                            <h2>Additional Bank Account {{ index + 1 }}</h2>
                            <div class="text-red cursor-pointer" @click="deleteAdditionalBankingDetails(index)">
                                Delete
                            </div>
                        </div>

                        <div class="mb-6">
                            <InputComponent
                                label="Additional Account Name"
                                placeholder="Additional Account Name"
                                name="other_account_name"
                                v-model="item.account_name"
                                @change="updateBankingDetails(item)"
                            />
                        </div>

                        <div class="mb-6">
                            <InputComponent
                                label="Additional Account Number"
                                placeholder="Additional Account Number"
                                name="other_account_number"
                                v-model="item.account_number"
                                @change="updateBankingDetails(item)"
                            />
                        </div>

                        <div class="mb-6 w-1/2">
                            <InputComponent
                                label="Additional Sort Code"
                                placeholder="00-00-00"
                                name="other_sort_code"
                                v-model="item.sort_code"
                                @change="updateBankingDetails(item)"
                            />
                        </div>

                        <div class="mb-6 flex">
                            <div class="w-1/2 mr-2">
                                <InputComponent
                                    label="IBAN"
                                    placeholder="IBAN"
                                    name="other_iban"
                                    rules="optional"
                                    v-model="item.iban"
                                    @change="updateBankingDetails(item)"
                                />
                            </div>

                            <div class="w-1/2 ml-2">
                                <InputComponent
                                    label="BIC/SWIFT"
                                    placeholder="BIC/SWIFT"
                                    name="other_bic_swift"
                                    rules="optional"
                                    v-model="item.bic_swift"
                                    @change="updateBankingDetails(item)"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </transition-group>

            <div class="mb-6">
                <AddNewButtonComponent
                    label="Add Bank Account"
                    @click="addAdditionalBankingDetails"
                    :disabled="bankingDetails.length >= 5"
                />
            </div>
        </ValidationObserver>
    </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { ValidationObserver } from "vee-validate";
import InputComponent from "@components/inputs/InputComponent";
import stepperValidationService from "@services/stepperValidationService";

export default {
    name: "BankingDetailsFormComponent",
    props: {},
    components: {
        ValidationObserver,
    },
    props: {
        step: { default: "" },
    },
    computed: {
        ...mapGetters(["bankingDetailsState"]),
    },
    data: function () {
        return {
            bankingDetails: [
                {
                    account_name: "",
                    account_number: "",
                    sort_code: "",
                    iban: "",
                    bic_swift: "",
                },
            ],
            showAdditionalBankAccountFields: false,
            isFormValidated: false,
        };
    },
    methods: {
        ...mapActions([
            "doGetBankingDetails",
            "doUpdateBankingDetails",
            "doAddAdditionalBankingDetails",
            "doRemoveAdditionalBankingDetails",
        ]),
        updateBankingDetails(bankingDetails) {
            this.doUpdateBankingDetails(bankingDetails);
        },
        deleteAdditionalBankingDetails(index) {
            this.doRemoveAdditionalBankingDetails(index);
        },
        addAdditionalBankingDetails() {
            this.bankingDetails.push({
                account_name: "",
                account_number: "",
                sort_code: "",
                iban: "",
                bic_swift: "",
            });
        },
        doValidateForm() {
            stepperValidationService.checkStepperStepStatus(
                this.step,
                this.bankingDetails,
                this.$refs.bankingDetailsObserver,
                false,
                true
            );
        },
    },
    mounted() {
        this.$root.$on("submittingApplication", () => this.doValidateForm());
        this.$root.$on("stepChanged", (step) => {
            if (this.step < step && !this.isFormValidated) {
                this.doValidateForm();
                this.isFormValidated = true;
            } else if (this.step == step) {
                this.isFormValidated = false;
            }
        });

        this.doGetBankingDetails().then((response) => {
            if (response.length) {
                this.bankingDetails = response;
            }
        });

        this.$gtm.trackView('Banking Details', 'currentPath');
    },
    watch: {
        bankingDetails() {
            stepperValidationService.checkStepperStepStatus(
                this.step,
                this.bankingDetails,
                this.$refs.bankingDetailsObserver
            );
        },
        bankingDetailsState() {
            this.bankingDetails = this.bankingDetailsState
        },
    },
};
</script>
